/**
 * Cards Settings - Domain-specific configuration for cards
 * Loads settings from the domain's settings.js file
 */

// Minimal fallback settings - only for critical safety when domain settings can't be loaded
// Domain-specific settings in each domain's settings.js are the source of truth
// Each domain controls: ENABLED, BANNER_DELAY, BANNER_DISPLAY, PARTNERS, etc.
const DEFAULT_CARDS_SETTINGS = {
    ENABLED: false  // Safety fallback: disable cards if domain settings can't be loaded
};

/**
 * Get cards settings from the domain's settings.js
 * Domain settings are the source of truth - no duplication here
 */
export async function getCardsSettings() {
    try {
        // Try to get settings from the current domain's settings.js
        const domainSettings = await getDomainSettings();

        // Domain settings are the source of truth - use them directly
        // Only merge with minimal safety fallback (ENABLED: false)
        if (domainSettings.CARDS_SETTINGS) {
            return {
                ...DEFAULT_CARDS_SETTINGS,
                ...domainSettings.CARDS_SETTINGS
            };
        }

        // If no domain settings found, return safety fallback
        return DEFAULT_CARDS_SETTINGS;

    } catch (error) {
        console.warn('Failed to load domain cards settings, using defaults:', error);
        return DEFAULT_CARDS_SETTINGS;
    }
}

/**
 * Get domain settings dynamically
 */
async function getDomainSettings() {
    try {
        // Try to get settings from the current page's context
        if (window.DOMAIN_SETTINGS && window.DOMAIN_SETTINGS.CARDS_SETTINGS) {
            return window.DOMAIN_SETTINGS;
        }

        // Try to import settings from the current domain's settings.js
        // This is a more reliable approach than fetching and parsing
        try {
            const settingsModule = await import('/settings.js');
            if (settingsModule.CARDS_SETTINGS) {
                return { CARDS_SETTINGS: settingsModule.CARDS_SETTINGS };
            }
        } catch (importError) {
            console.warn('Could not import settings module:', importError);
        }

    } catch (error) {
        console.warn('Could not load domain settings:', error);
    }

    return { CARDS_SETTINGS: null }; // Return null to indicate no domain settings found
}

/**
 * Check if cards are enabled globally
 */
export function isCardsEnabled() {
    return getCardsSettings().then(settings => settings.ENABLED);
}

/**
 * Check if a specific partner is enabled
 */
export function isPartnerEnabled(partner) {
    return getCardsSettings().then(settings => {
        return settings.PARTNERS && settings.PARTNERS[partner] === true;
    });
}

/**
 * Get all enabled partners
 */
export function getEnabledPartners() {
    return getCardsSettings().then(settings => {
        if (!settings.PARTNERS) return [];
        return Object.keys(settings.PARTNERS).filter(partner =>
            settings.PARTNERS[partner] === true
        );
    });
}

